import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { FormikProps } from 'formik';

import AppLanguageAvatar from '../../../components/designe/app-entity-avatar/app-language-avatar/AppExerciseAvatar';
import AppSwitch from '../../../components/designe/app-switch/AppSwitch';
import AppFormSelectWrapper from '../../../components/designe/forms/app-form-select-wrapper';
import AppFormTextField from '../../../components/designe/forms/app-form-text-field';
import {
  AccessScope,
  AvatarSize,
  AvatarTypes,
  PostsInteractionScope,
  PrimaryColor,
  ThemeMode,
} from '../../../shared/constants';
import { getSelectableUnitsForParameter } from '../../../shared/constants/user-measurements';
import { PRIMARY_COLOR_MAP } from '../../../shared/primary-color-map';
import { ILanguage } from '../../../types/interfaces/language';
import AuthTextField from '../components/auth-text-field';
import { useStyles } from './CompleteRegistration.styles';
import {
  IRegistrationWizardValues,
  RegistrationWizardStep,
} from './complete-registration.types';

interface IRegistrationWizardStepsProps {
  step: RegistrationWizardStep;
  formik: FormikProps<IRegistrationWizardValues>;
  languages: ILanguage[] | undefined;
}

function getLanguageItem(language: ILanguage) {
  return (
    <Box display="flex" gap={1} alignItems="center">
      <AppLanguageAvatar
        language={language}
        size={AvatarSize.s}
        type={AvatarTypes.round}
      />
      <Typography variant="text-md">{language?.title}</Typography>
    </Box>
  );
}

export default function RegistrationWizardSteps({
  step,
  formik,
  languages,
}: IRegistrationWizardStepsProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { classes } = useStyles(theme);

  const pagePrivacyOptions = useMemo(
    () =>
      Object.values(AccessScope).map((scope) => ({
        id: scope,
        name: t(`settings.pagePrivacy_${scope}`),
      })),
    [t],
  );

  const postPrivacyOptions = useMemo(
    () =>
      Object.values(PostsInteractionScope).map((scope) => ({
        id: scope,
        name: t(`settings.postPrivacy_${scope}`),
      })),
    [t],
  );

  const commentPrivacyOptions = useMemo(
    () =>
      Object.values(PostsInteractionScope).map((scope) => ({
        id: scope,
        name: t(`settings.commentPrivacy_${scope}`),
      })),
    [t],
  );

  const renderMeasurementField = (
    parameter: 'weight' | 'height',
    valueName: 'weightValue' | 'heightValue',
    unitName: 'weightUnitKey' | 'heightUnitKey',
    labelKey: string,
  ) => {
    const unitOptions = getSelectableUnitsForParameter(parameter).map(
      (unitKey) => ({
        id: unitKey,
        name: t(`settings.measurements.units.${unitKey}`),
      }),
    );

    return (
      <Box className={classes.measurementRow}>
        <Typography variant="text-md">{t(labelKey)}</Typography>
        <Box className={classes.measurementFields}>
          <AppFormTextField
            fullWidth
            type="number"
            name={valueName}
            formik={formik}
            mode="dark"
          />
          <AppFormSelectWrapper
            options={unitOptions}
            name={unitName}
            formik={formik}
            disabled
            disableClear
            disableSearch
            disableSorting
            mode="dark"
          />
        </Box>
      </Box>
    );
  };

  switch (step) {
    case RegistrationWizardStep.name:
      return (
        <Box className={classes.stepContent}>
          <AuthTextField
            placeholder={t('common.firstname')}
            fullWidth
            name="firstname"
            formik={formik}
            className={classes.textField}
          />
          <AuthTextField
            placeholder={t('common.lastname')}
            fullWidth
            name="lastname"
            formik={formik}
            className={classes.textField}
          />
        </Box>
      );

    case RegistrationWizardStep.measurements:
      return (
        <Box className={classes.stepContent}>
          {renderMeasurementField(
            'weight',
            'weightValue',
            'weightUnitKey',
            'settings.measurements.weight',
          )}
          {renderMeasurementField(
            'height',
            'heightValue',
            'heightUnitKey',
            'settings.measurements.height',
          )}
        </Box>
      );

    case RegistrationWizardStep.visualization:
      return (
        <Box className={classes.stepContent}>
          <Box className={classes.colorSection}>
            <Typography variant="text-md">
              {t('settings.visualization.primaryColor')}
            </Typography>
            <Box className={classes.colorCirclesRow}>
              {(Object.values(PrimaryColor) as PrimaryColor[]).map((color) => (
                <Box
                  key={color}
                  className={`${classes.colorCircle} ${
                    formik.values.primaryColor === color
                      ? classes.colorCircleSelected
                      : ''
                  }`}
                  sx={{ backgroundColor: PRIMARY_COLOR_MAP[color].main }}
                  onClick={() => formik.setFieldValue('primaryColor', color)}
                  role="button"
                  aria-pressed={formik.values.primaryColor === color}
                  aria-label={color}
                />
              ))}
            </Box>
          </Box>

          <Box className={classes.themeRow}>
            <Typography variant="text-md">
              {t('settings.visualization.theme')}
            </Typography>
            <AppSwitch
              value={formik.values.theme === ThemeMode.light}
              onChange={() =>
                formik.setFieldValue(
                  'theme',
                  formik.values.theme === ThemeMode.light
                    ? ThemeMode.dark
                    : ThemeMode.light,
                )
              }
            />
          </Box>

          <AppFormSelectWrapper
            label={t('common.language')}
            className={classes.textField}
            options={languages}
            fieldName="title"
            disableClear
            disableSearch
            defaultValue={languages?.find(
              (language) => language.id === formik.values.language?.id,
            )}
            name="language"
            formik={formik}
            renderOption={getLanguageItem}
          />
        </Box>
      );

    case RegistrationWizardStep.privacy:
      return (
        <Box className={classes.stepContent}>
          <AppFormSelectWrapper
            label={t('settings.pagePrivacy')}
            options={pagePrivacyOptions}
            disableClear
            disableSearch
            defaultValue={pagePrivacyOptions.find(
              (option) => option.id === formik.values.scope,
            )}
            onChange={(event) =>
              formik.setFieldValue('scope', event.target.value?.id)
            }
            name="scope"
            formik={formik}
          />
          <AppFormSelectWrapper
            label={t('settings.whoCanAddPost')}
            options={postPrivacyOptions}
            disableClear
            disableSearch
            defaultValue={postPrivacyOptions.find(
              (option) => option.id === formik.values.postsScope,
            )}
            onChange={(event) =>
              formik.setFieldValue('postsScope', event.target.value?.id)
            }
            name="postsScope"
            formik={formik}
          />
          <AppFormSelectWrapper
            label={t('settings.whoCanComment')}
            options={commentPrivacyOptions}
            disableClear
            disableSearch
            defaultValue={commentPrivacyOptions.find(
              (option) => option.id === formik.values.commentsScope,
            )}
            onChange={(event) =>
              formik.setFieldValue('commentsScope', event.target.value?.id)
            }
            name="commentsScope"
            formik={formik}
          />
        </Box>
      );

    default:
      return null;
  }
}

export function getRegistrationStepTitle(
  step: RegistrationWizardStep,
  t: (key: string) => string,
): string | null {
  switch (step) {
    case RegistrationWizardStep.name:
      return t('signUp.completeRegistration.stepUserInfoTitle');
    case RegistrationWizardStep.measurements:
      return t('signUp.completeRegistration.stepMeasurementsTitle');
    case RegistrationWizardStep.visualization:
      return t('signUp.completeRegistration.stepVisualizationTitle');
    case RegistrationWizardStep.privacy:
      return t('signUp.completeRegistration.stepPrivacyTitle');
    default:
      return null;
  }
}
